import {
  BadgeCard,
  type BadgeCardRow,
  Button,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  ImportIcon,
  LazyLoader,
  LockIcon,
  Modal,
  type Row,
  showToast,
  Table,
  type TableColumns,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { type FC, Fragment, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayoutContext } from '../../../contexts/AppLayoutContextProvider';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useFilterTags } from '../../../hooks/useFilterTags';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  type GroupedTeachingBlockClassGridCard,
  useTeachingBlockDataCards,
} from '../../../hooks/useTeachingBlockDataCards';
import { LessonEditStatus } from '../../../types/planung-graphql-client-defs';
import { createIntArray, uniqueBy } from '../../../utils/arrayFunc';
import type { LessonInfo } from '../../../utils/calculateLessonInfos';
import { isBadgeCardWidth } from '../../../utils/typeguards';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { refreshTeachingBlockVersionQueries } from '../../TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { TeachingBlockCardsForm } from '../../TeachingBlockVersion/TeachingBlockVersionCards/TeachingBlockCardsForm';
import { TeachingBlockLessonForm } from '../Forms/TeachingBlockLessonForm';
import { useTeachingBlockLessons } from '../hooks/useTeachingBlockLessons';
import { ROW_HEIGHT_CARDS_IN_ROW } from '../utils/estimatedRowHeight';
import { ImportLessonsToTeachingBlockVersionModal } from './LessonsImport/ImportLessonsToTeachingBlockVersionModal';

export type ClassInterfaceType = {
  uuid?: string;
  grade?: number;
  gradeGroup?: { uuid?: string; name: string; shortName?: string } | null;
  name: string;
  shortName?: string;
};

export type LessonTableType = {
  uuid: string;
  subject: string;
  subjectShortName: string;
  subjectUuid: string;
  editStatus?: LessonEditStatus;
  classes: Array<
    ClassInterfaceType & {
      groups?: Array<{ uuid: string; shortName?: string; name: string; classFraction?: number }>;
    }
  >;
  classesUuids: string[];
  teachers: { uuid: string; name?: string | null; color?: string | null }[];
  teacherUuids: string[];
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
  onlyInTeachingBlockVersion: boolean;
  lessonUnits?: {
    uuid: string;
    duration: number;
    count: number;
    subjectContainer?: { uuid: string; name: string; shortName: string } | null;
  }[];
  lessonInfo?: LessonInfo;
  groupClassNames?: string;
  teachingLoadFactors?: string;
  placedCardsCount?: number | null;
  isEpochPlan?: boolean;
  curriculumUuid?: string;
  schoolYearUuid?: string;
};

type LessonTableProps = {
  versionUuid: string;
};

export const TeachingBlockVersionLessonsTable: FC<LessonTableProps> = observer(({ versionUuid }) => {
  const { pimAuthClaims } = useAuthClaims();
  const isMobile = useIsMobile();
  const { scrollRef } = useContext(AppLayoutContext);

  const { t } = useTranslation();

  const store = useTeachingBlockStore();

  const schoolYear = useSelectedSchoolYear();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('lessons-table', {
    elective: false,
    teachingLoadEnabled: false,
    timetableEnabled: false,
  });

  const teachingBlockCardsContext = useMemorizedCacheTag('TEACHING_BLOCK_CARDS');

  const { classGridCards } = useTeachingBlockDataCards(versionUuid);

  const groupedByLessonUuid = useMemo(() => {
    const grouped: GroupedTeachingBlockClassGridCard = {
      lessons: [],
    };

    classGridCards.forEach((card) => {
      const lessonIndex = grouped.lessons.findIndex((lesson) => lesson.lessonUuid === card.lessonUuid);
      if (lessonIndex === -1) {
        grouped.lessons.push({
          lessonUuid: card.lessonUuid,
          cards: {
            byDuration: [
              {
                duration: card.duration ?? 1,
                cards: [card],
                teachingBlockCardsCount: 1,
                biggestDivisionsCount: card.divisionsCount ?? 0,
              },
            ],
          },
        });
      } else {
        const durationIndex = grouped.lessons[lessonIndex].cards.byDuration.findIndex(
          (duration) => duration.duration === card.duration,
        );
        if (durationIndex === -1) {
          grouped.lessons[lessonIndex].cards.byDuration.push({
            duration: card.duration ?? 1,
            cards: [card],
            teachingBlockCardsCount: 1,
            biggestDivisionsCount: card.divisionsCount ?? 0,
          });
        } else {
          const currentDuration = grouped.lessons[lessonIndex].cards.byDuration[durationIndex];
          currentDuration.cards.push(card);
          currentDuration.teachingBlockCardsCount = uniqueBy(currentDuration.cards, 'teachingBlockCardUuid').length;
          currentDuration.biggestDivisionsCount = Math.max(
            currentDuration.biggestDivisionsCount,
            card.divisionsCount ?? 0,
          );
        }
      }
    });

    // sort by duration
    grouped.lessons.forEach((lesson) => {
      lesson.cards.byDuration.sort((a, b) => a.duration - b.duration);
    });

    return grouped;
  }, [classGridCards]);

  const { sorting, saveSorting } = useColumnsSort(`lessons-table-${versionUuid}`);

  const [currentLessonUuid, setCurrentLessonUuid] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [cardsModalOpen, setCardsModalOpen] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);

  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  const { memoFilter: dataFilter, filterTags } = useFilterTags(versionUuid);

  const {
    detachLessons,
    deleteCardsAndLessons,
    deactivateLessons,
    assignLessons,
    updateCheckboxes: _updateCheckboxes,
    loadingState,
    duplicateLesson,
    lessonsData,
    teachingBlockSubjectContainerUuid,
    attachLessons,
    createCards,
    dispatch,
  } = useTeachingBlockLessons({
    versionUuid: versionUuid,
  });

  const { teacherData } = useLoadBasicData({ pause: !schoolYear });

  const filteredLessonData = lessonsData
    .filter(dataFilter)
    .sort((a, b) => a.classSort.localeCompare(b.classSort) || a.subject.localeCompare(b.subject));

  const updateCheckboxes = useCallback(
    async ({
      type,
      uuids,
      value,
    }: {
      type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
      uuids: string[];
      value: boolean;
    }) => {
      await _updateCheckboxes({
        type,
        uuids,
        value,
      });
    },
    [_updateCheckboxes],
  );

  const { confirm, ConfirmationDialog } = useConfirm();

  const handleDelete = async (lessons: LessonTableType[]) => {
    const placedCards = lessons.some((l) => l.placedCardsCount && l.placedCardsCount > 0);

    const lessonsNames = lessons.map((l) => l.subject).join(', ');
    const text = placedCards
      ? t('lesson.confirm.delete.hasPlacedCards', { count: lessons.length, names: lessonsNames })
      : `${t('lesson.confirm.delete.default', { count: lessons.length })} ${lessonsNames}`;

    const confirmed = await confirm({ message: text });
    if (confirmed) {
      const response = await deleteCardsAndLessons(lessons.map((l) => l.uuid));
      if (response.error) {
        showToast(t('lesson.message.delete.error'), { type: 'error' });
      } else {
        showToast(t('lesson.message.delete.success'), { type: 'success' });
      }
    }
  };

  const handleDuplicate = async (lessons: LessonTableType[]) => {
    const notOnlyInVersion = lessons.some((l) => !l.onlyInTeachingBlockVersion);
    const uuids = lessons.map((l) => l.uuid);

    if (loadingState.some(({ uuid, loading }) => loading && uuids.includes(uuid))) {
      return;
    }

    const confirmed = await confirm({
      title: notOnlyInVersion ? t('timetableVersion.editInVersion') : t('common.duplicate'),
      message: notOnlyInVersion
        ? t('lesson.confirm.editInVersion.confirm', { count: lessons.length })
        : t('lesson.confirm.duplicate.confirm', { count: lessons.length }),
    });
    if (confirmed) {
      const response = await duplicateLesson(uuids, notOnlyInVersion);

      if (response) {
        if (response.error) {
          showToast(t('lesson.message.duplicate.error'), { type: 'error' });
        } else {
          showToast(t('lesson.message.duplicate.success'), { type: 'success' });
        }
      } else {
        showToast(t('lesson.message.duplicate.error'), { type: 'error' });
      }
    }
  };

  const handleDeactivate = async (lessons: LessonTableType[]) => {
    const placedCards = lessons.some((l) => l.placedCardsCount && l.placedCardsCount > 0);

    const text = placedCards
      ? t('lesson.confirm.deactivate.hasPlacedCards', { count: lessons.length })
      : t('lesson.confirm.deactivate.default', { count: lessons.length });

    const confirmed = await confirm({ message: text });
    if (confirmed) {
      const response = await deactivateLessons(lessons.map((l) => l.uuid));
      if (response.error) {
        showToast(t('lesson.message.deactivate.error'), { type: 'error' });
      } else {
        showToast(t('lesson.message.deactivate.success'), { type: 'success' });
      }
    }
  };

  const handleDetach = async (uuids: string[]) => {
    const response = await detachLessons(uuids);

    if (response.error) {
      showToast(t('lesson.message.detach.error'), { type: 'error' });
    } else {
      showToast(t('lesson.message.detach.success'), { type: 'success' });
    }
  };

  const handleAssign = async (item: LessonTableType, teachers: { uuid: string }[]) => {
    await assignLessons([item.uuid], teachers);
  };

  const handleAdd = () => {
    setEditModalOpen(true);
  };

  const handleEdit = (uuid: string) => {
    setCurrentLessonUuid(uuid);
    setEditModalOpen(true);
  };

  const handleClose = () => {
    setCurrentLessonUuid(null);
    setCardsModalOpen(false);
    setEditModalOpen(false);
  };

  const handleImport = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const response = await attachLessons(uuids);

    // Je Einheit an einer Schiene entsteht ihre Zahl an ungesteckten Karten.
    await Promise.all(
      response.lessons.map(async (l) => {
        const durations = l.lessonUnits
          .filter(({ subjectContainer }) => subjectContainer?.uuid)
          .flatMap((unit) => Array.from({ length: Math.abs(unit.count ?? 0) }, () => unit.duration ?? 1));
        if (durations.length > 0) {
          await createCards({ versionUuid, lessonUuid: l.uuid, durations }, teachingBlockCardsContext);
        }
      }),
    );
    await refreshTeachingBlockVersionQueries(versionUuid);

    if (response.error) {
      showToast(t('lesson.message.import.error'), { type: 'error' });
    } else {
      showToast(t('lesson.message.import.success'), { type: 'success' });
    }
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
  };

  const tableColumns = useMemo((): TableColumns<LessonTableType>[] => {
    return [
      {
        header: t('lesson.table.subject'),
        id: 'subject',
        accessorKey: 'subject',
        meta: {
          filterName: t('lesson.table.subject'),
        },
        size: 200,
      },
      {
        header: t('common.week', { count: 0 }),
        id: 'cards',
        size: 150,
        enableSorting: false,
        canOverflow: true,
        isMultiline: true,
        cell: ({ row }) => {
          const uuid = row.original.uuid;

          const lessonCards = groupedByLessonUuid.lessons.filter((g) => g.lessonUuid === uuid);

          if (lessonCards && lessonCards.length > 0) {
            const cardsByDuration = lessonCards[0].cards.byDuration;
            return (
              <>
                {cardsByDuration.map((items, index) => {
                  const cardToRender = items.cards.find((c) => c.divisionsCount === items.biggestDivisionsCount);
                  if (cardToRender) {
                    const postions: number[] = [];
                    items.cards
                      .filter((c) => c.classUuid === cardToRender.classUuid)
                      .forEach((c) => {
                        if ((c.divisionsCount ?? 1) > 1) {
                          postions.push(c.usedPosition ?? 1);
                        } else {
                          postions.push(0);
                        }
                      });
                    const rows: BadgeCardRow[] = createIntArray(items.biggestDivisionsCount ?? 1).map((i) => {
                      if (postions.includes(i)) {
                        return {
                          rowColor: '',
                          colColors: cardToRender.colors,
                        };
                      }
                      return {
                        rowColor: '',
                        colColors: [],
                      };
                    });
                    return (
                      <div key={cardToRender.uuid} className={'mt-2 mb-2'}>
                        <BadgeCard
                          count={items.teachingBlockCardsCount}
                          color={cardToRender.textColor ?? ''}
                          width={isBadgeCardWidth(cardToRender.duration) ? cardToRender.duration : 6}
                          label={`${cardToRender.duration} ${t('common.week', { count: cardToRender.duration ?? 1 })}`}
                          rows={rows}
                        />
                      </div>
                    );
                  }
                  return <Fragment key={index}></Fragment>;
                })}
              </>
            );
          }
          return '-';
        },
      },
      {
        header: t('lesson.table.classes'),
        id: 'classes',
        size: 175,
        cell: ({ row }) => {
          const shortNames = row.original.classes.map((node) => node.name);
          return <>{shortNames.join(', ')}</>;
        },
        accessorFn: (data) => {
          const shortNames = data.classes.map((node) => node.shortName);
          return shortNames.join(', ');
        },
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'groupClassNames',
        accessorKey: 'groupClassNames',
        size: 175,
        isMultiline: true,
      },
      {
        header: t('lesson.table.teacher'),
        id: 'teachers',
        cell: ({ row }) => {
          const displayNamesShort = row.original.teachers.map((teacher) => teacher.name);
          return <>{displayNamesShort.join(', ')}</>;
        },
        accessorFn: (originalRow) => {
          const displayNamesShort = originalRow.teachers.map((teacher) => teacher.name);
          return displayNamesShort.join(', ');
        },
        size: 175,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTimetableEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTimetableEnabled.full'),
          tooltip: t('lesson.basics.isTimetableEnabled.full'),
        },
        id: 'timetableEnabled',
        accessorKey: 'timetableEnabled',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('deputate.title', { count: 1 }),
        id: 'editStatus',
        accessorKey: 'editStatus',
        meta: {
          filterName: t('common.editable.full'),
          tooltip: t('common.editable.full'),
        },
        size: 60,
        cell: ({ row }) => {
          return row.original.editStatus === LessonEditStatus.Blocked ? (
            <Chip bgColor={'var(--chip-bg-color)'} value={<LockIcon className={'smallest'} />} />
          ) : undefined;
        },
      },
    ];
  }, [t, groupedByLessonUuid]);

  const defaultDropdownItems = (row: Row<LessonTableType>): DropdownMenuItem[] => [
    {
      label: t('timetableVersion.editInVersion'),
      onClick: async () => {
        await handleDuplicate([row.original]);
      },
    },
    {
      label: t('teachingBlockVersion.editCards'),
      onClick: () => {
        setCurrentLessonUuid(row.original.uuid);
        setCardsModalOpen(true);
      },
    },
    {
      label: t('timetableVersion.deactivate'),
      type: 'default',
      color: 'error',
      onClick: async () => {
        await handleDeactivate([row.original]);
      },
    },
  ];

  const inVersionDropdownItems = (row: Row<LessonTableType>): DropdownMenuItem[] => {
    const qualified = teacherData?.people?.filter(
      (person) =>
        person.qualifications.some((q) => q.subjectUuid === row.original.subjectUuid) &&
        !row.original.teacherUuids.includes(person.uuid),
    );
    const teachers = qualified?.length
      ? qualified
      : teacherData?.people.filter((person) => !row.original.teacherUuids.includes(person.uuid));

    return [
      {
        label: t('lesson.edit'),
        onClick: () => {
          handleEdit(row.original.uuid);
        },
      },
      {
        label: t('teachingBlockVersion.editCards'),
        onClick: () => {
          setCurrentLessonUuid(row.original.uuid);
          setCardsModalOpen(true);
        },
      },
      {
        label: t('common.duplicate'),
        onClick: async () => {
          await handleDuplicate([row.original]);
        },
      },
      {
        label: t('lesson.actions.assignTo'),
        subContent: teachers
          ? teachers.map((teacher) => {
              return {
                type: 'default',
                label: teacher.selectName ?? '',
                onClick: () => {
                  handleAssign(row.original, [teacher]);
                },
              };
            })
          : [],
      },
      {
        label: t('lesson.actions.detach'),
        onClick: () => {
          handleDetach([row.original.uuid]);
        },
      },
      {
        type: 'ruler',
      },
      {
        label: t('lesson.actions.toggle.isElective'),
        type: 'switch',
        value: row.original.elective ?? false,
        onValueChange: async () => {
          await updateCheckboxes({ type: 'elective', uuids: [row.original.uuid], value: !row.original.elective });
        },
      },
      {
        label: t('lesson.basics.isTeachingLoadEnabled.full'),
        type: 'switch',
        value: row.original.teachingLoadEnabled ?? false,
        onValueChange: async () => {
          await updateCheckboxes({
            type: 'teachingLoadEnabled',
            uuids: [row.original.uuid],
            value: !row.original.teachingLoadEnabled,
          });
        },
      },
      {
        label: t('lesson.basics.isTimetableEnabled.full'),
        type: 'switch',
        disabled: true,
        value: row.original.timetableEnabled ?? false,
        onValueChange: async () => {
          await updateCheckboxes({
            type: 'timetableEnabled',
            uuids: [row.original.uuid],
            value: !row.original.timetableEnabled,
          });
        },
      },
      {
        type: 'ruler',
      },
      {
        label: t('common.delete'),
        type: 'default',
        color: 'error',
        onClick: () => {
          handleDelete([row.original]);
        },
      },
    ];
  };

  const importText = t('lesson.import');

  const actionBarSettings = useMemo(() => {
    return {
      extendedActionsLeft: <>{filterTags.map((tag) => tag)}</>,
      extendedActionsRight: (
        <Button
          hierarchy={'tertiary'}
          onClick={() => setImportModalOpen(true)}
          icon={<ImportIcon />}
          disabled={store.readonly}
        >
          {importText}
        </Button>
      ),
      showExpertFilter: true,
      showAddButton: true,
      disableAddButton: store.readonly,
      showPrintButton: true,
      showBulkEdit: false,
      marginBottom: 'var(--spacing-3)',
    };
  }, [filterTags, importText, store.readonly]);

  return (
    <>
      <Table<LessonTableType>
        // Ohne Seiten: alle Zuordnungen in einer Liste, gerendert werden nur die sichtbaren Zeilen
        // (wie in der Zuordnungs-Tabelle der Stundenplan-Version).
        // mobil nicht: der Kopf klebt per position: fixed und liefe beim seitlichen Wischen nicht mit
        stickyHeader={!isMobile}
        stickyHeaderOptions={{
          top: 60, // height: var(--header-height)
        }}
        virtualize={true}
        virtualizerOptions={{
          getScrollElement: () => {
            return scrollRef?.current ?? null;
          },
          measureElement: (element) => {
            return element.getBoundingClientRect().height;
          },
          estimateSize: () => ROW_HEIGHT_CARDS_IN_ROW,
        }}
        showBorderRadius
        showShadow
        isDesktop={true}
        showVisibility
        loading={loadingState?.some(({ uuid, loading }) => uuid === 'all' && loading)}
        isOnWhite={false}
        onAddClick={handleAdd}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('lesson.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
          filename: `${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
        }}
        showActionBar
        actionBarSettings={actionBarSettings}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        showSort={true}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        sorting={sorting}
        onSortingChange={saveSorting}
        columns={tableColumns}
        data={filteredLessonData}
        lastColWidth='34px'
        lastCol={(row) => {
          return (
            <>
              {row.original.onlyInTeachingBlockVersion ? (
                <Dropdown
                  disabled={
                    store.readonly || loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)
                  }
                  trigger={
                    <Button
                      disabled={store.readonly}
                      isLoading={loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={inVersionDropdownItems(row)} />
                </Dropdown>
              ) : (
                <Dropdown
                  disabled={
                    store.readonly || loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)
                  }
                  trigger={
                    <Button
                      disabled={store.readonly}
                      isLoading={loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={defaultDropdownItems(row)} />
                </Dropdown>
              )}
            </>
          );
        }}
      />
      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => handleClose()}
        title={currentLessonUuid ? t('lesson.edit') : t('lesson.add')}
      >
        {editModalOpen && versionUuid && (
          <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
            <TeachingBlockLessonForm
              closeForm={() => handleClose()}
              lessonUuid={currentLessonUuid}
              defaultInitialValues={{ timetableEnabled: true }}
              versionUuid={versionUuid}
            />
          </Suspense>
        )}
      </Modal>
      {cardsModalOpen && (
        <Modal
          isOpen={cardsModalOpen}
          onRequestClose={() => handleClose()}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          title={t('teachingBlockVersion.editCards') as string}
        >
          {currentLessonUuid && teachingBlockSubjectContainerUuid && (
            <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
              <TeachingBlockCardsForm
                subjectContainerUuid={teachingBlockSubjectContainerUuid}
                onClose={() => handleClose()}
                lessonUuid={currentLessonUuid}
                versionUuid={versionUuid}
              />
            </Suspense>
          )}
        </Modal>
      )}
      {versionUuid && teachingBlockSubjectContainerUuid && (
        <ImportLessonsToTeachingBlockVersionModal
          onClose={() => {
            setImportModalOpen(false);
          }}
          isOpen={importModalOpen}
          onSubmit={handleImport}
          teachingBlockSubjectContainerUuid={teachingBlockSubjectContainerUuid}
          versionUuid={versionUuid}
        />
      )}
      <ConfirmationDialog />
    </>
  );
});
